import { AssetCodeFormatter } from '../../../../components/AssetCodeFormatter';
import Button from '../../../../components/button/Button';
import { AccountFeeInfo, MakePaymentForm, PaymentStepProps } from './MakePayment';
import styles from './MakePayment.module.scss';
import api, { ApiResponse, isAxiosErrorHandled } from '../../../../api';
import { endpoints } from '../../../../endpoints.config';
import { Toast } from '../../../../helpers/toast';
import { useMemo, useState } from 'react';
import { getFeeLabel } from './AmountPurpose';
import { getRelevantPayloadFields, useIsFinancialInstitution } from '../helpers';
import { MakePaymentMfaModal } from './MakePaymentMfaModal';
import { ItemConnectionModel } from 'pages/IbaneraNetwork/items/ItemConnection';
import { PaymentStatus } from 'pages/IbaneraNetwork/models/models';
import { useGetFiatAccountDetails } from 'helpers/useGetFiatAccountDetails';
import { useTranslation } from 'react-i18next';
import { SelectOption } from 'models/formSingleSelectField';
import { getErrorMessage } from 'errors';

type Props = PaymentStepProps & {
    data: MakePaymentForm;
    purposes: SelectOption[];
    accountFeeInfo: AccountFeeInfo;
    closeHandler: (paymentStatus?: PaymentStatus) => void;
    connectionPayee: ItemConnectionModel;
};

export const ReviewConnectionPayment: React.FC<Props> = ({
    data,
    purposes,
    accountFeeInfo,
    nextStep,
    closeHandler,
    connectionPayee,
}) => {
    const { t } = useTranslation('common');
    const bFinancialInstitution = useIsFinancialInstitution();
    const assetAccountDetail = useGetFiatAccountDetails({
        accountId: data.sourceCustomerAssetAccountsId,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const purpose = useMemo(
        () => purposes.find((item) => item.value === data?.purpose),
        [data?.purpose, purposes]
    );
    const [showMfaModal, setShowMfaModal] = useState(false);
    const openMfaModal = () => setShowMfaModal(true);
    const closeMfaModal = () => setShowMfaModal(false);
    const feeOptions = useMemo(
        () =>
            accountFeeInfo.transferTypes.find(
                (fee) => fee.transferType.toLowerCase() === data.transferType.toLowerCase()
            )?.feeOptions ?? [],
        [accountFeeInfo?.transferTypes, data?.transferType]
    );
    const fee = useMemo(
        () => feeOptions.find((fee) => fee.bankFeeSplitType === data.feeId),
        [data.feeId, feeOptions]
    );

    const handlePaymentError = (err: any) => {
        if (!isAxiosErrorHandled(err)) {
            Toast.openGenericErrorToast();
            return;
        }

        const errors = Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors
            : [];
        const tfaError = errors.some(
            (error: any) =>
                error?.messageCode === 'TfaCode_Invalid' ||
                error?.fieldName?.toLowerCase() === 'tfacode'
        );

        if (tfaError) {
            Toast.openErrorToast(getErrorMessage('TfaCode_Invalid'));
            return;
        }

        const messageCode = errors.find((error: any) => error?.messageCode)?.messageCode;
        if (messageCode) {
            Toast.openErrorToast(getErrorMessage(messageCode));
            return;
        }

        Toast.openGenericErrorToast();
    };

    const submitPayment = async (tfaCode: string, tfaType: string) => {
        setIsSubmitting(true);
        closeMfaModal();
        if (data.sourceCustomerAssetAccountsId == null || assetAccountDetail?.id == null) {
            Toast.openGenericErrorToast();
            setIsSubmitting(false);
            return;
        }
        const {
            id,
            paymentReference,
            attachments,
            amount,
            feeId,
            destinationAmount,
            notes,
            purpose,
        } = getRelevantPayloadFields(data);
        if (
            assetAccountDetail?.productDisplayName !==
            data?.payeeConnectionAssetAccount?.productDisplayName
        ) {
            const apiParmsNew = {
                FromCustomerAssetAccountsId: data.sourceCustomerAssetAccountsId,
                ToCustomerAssetAccountsId: data?.payeeConnectionAssetAccount?.id,
                Amount: amount ?? destinationAmount,
                Reference: paymentReference,
                Purpose: purpose,
                TfaCode: tfaCode,
                TfaType: tfaType,
            };
            const formData = new FormData();
            Object.entries(apiParmsNew).forEach(([key, value]) => {
                formData.append(key, value as any); // cast `value` if necessary
            });

            try {
                const res = await api.post<
                    ApiResponse<{
                        bApproved: boolean;
                        id: string;
                    }>
                >(endpoints.networkmodule.createTransfer, formData, {
                    headers: { 'content-type': 'multipart/form-data' },
                });

                if (res.data.status === '1') {
                    closeHandler({
                        amount: amount + '',
                        recipient:
                            connectionPayee?.customerId == connectionPayee?.senderId
                                ? connectionPayee.receiverName
                                : connectionPayee.senderName,
                        transactionId: res.data?.id,
                    });

                    nextStep();
                }
            } catch (err) {
                handlePaymentError(err);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            const apiData = {
                FromCustomerAssetAccountsId: data.sourceCustomerAssetAccountsId,
                ToCustomerAssetAccountsId: data.payeeConnectionAssetAccount?.id,
                Amount: amount ?? destinationAmount,
                reference: paymentReference,
                purpose: purpose,
                TfaCode: tfaCode,
                TfaType: tfaType,
                note: notes ?? '',
            };

            const formData = new FormData();
            Object.entries(apiData).forEach(([key, value]) => {
                formData.append(key, value as any); // cast `value` if necessary
            });
            attachments.forEach((file) => {
                formData.append('attachments', file);
            });
            try {
                const res = await api.post<
                    ApiResponse<{
                        bApproved: boolean;
                        id: string;
                    }>
                >(endpoints.networkmodule.createTransfer, formData, {
                    headers: { 'content-type': 'multipart/form-data' },
                });

                if (res.data.status === '1') {
                    closeHandler({
                        amount: amount + '',
                        recipient:
                            connectionPayee?.customerId == connectionPayee?.senderId
                                ? connectionPayee.receiverName
                                : connectionPayee.senderName,
                        transactionId: res.data?.id,
                    });
                    nextStep();
                }
            } catch (err) {
                handlePaymentError(err);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className={styles.ReviewPayment}>
            <MakePaymentMfaModal
                open={showMfaModal}
                handleClose={closeMfaModal}
                handleSubmit={submitPayment}
            />
            <div className={styles.ReviewPaymentIbaneraNetwork}>
                <div className="Row">
                    <h4>Payer Details</h4>
                    <dl className={styles.Recipient}>
                        <div className={styles.Row}>
                            <dt>Account Name:</dt>
                            <dd>{assetAccountDetail?.accountName || '-'}</dd>
                        </div>
                    </dl>
                </div>
                <div className="Row">
                    <h4>Recipient Details</h4>
                    <dl className={styles.Recipient}>
                        <div className={styles.Row}>
                            <dt>Account Name:</dt>
                            <dd>{data?.payeeConnectionAssetAccount?.accountName || '-'}</dd>
                        </div>
                        <div className={styles.Row}>
                            <dt>Payee Name:</dt>
                            <dd>
                                {connectionPayee?.customerId == connectionPayee?.senderId
                                    ? connectionPayee.receiverName
                                    : connectionPayee.senderName}
                            </dd>
                        </div>

                        <div className={styles.Row}>
                            <dt>Payee Reference:</dt>
                            <dd>{data.payeesReference || '-'}</dd>
                        </div>
                        {data.swiftNumber && (
                            <div className={styles.Row}>
                                <dt>Swift Number:</dt>
                                <dd>{data?.payeeConnectionAssetAccount?.swiftBic}</dd>
                            </div>
                        )}
                        {data.iban && (
                            <div className={styles.Row}>
                                <dt>IBAN:</dt>
                                <dd>{data.iban}</dd>
                            </div>
                        )}
                        {data.routingNumber && (
                            <div className={styles.Row}>
                                <dt>Routing Number:</dt>
                                <dd>{data.routingNumber}</dd>
                            </div>
                        )}
                        {data?.payeeConnectionAssetAccount?.accountNumber && (
                            <div className={styles.Row}>
                                <dt>Account Number:</dt>
                                <dd>{data?.payeeConnectionAssetAccount?.accountNumber}</dd>
                            </div>
                        )}
                    </dl>
                </div>
                <div className="Row">
                    <h4>Payment details</h4>
                    <dl>
                        <div className={styles.Row}>
                            <dt>Payment Method:</dt>
                            <dd><span className="notranslate" translate="no">{data.transferType}</span></dd>
                        </div>
                        <div className={styles.Row}>
                            <dt>Payment Reference:</dt>
                            <dd>
                                <span className="notranslate" translate="no">
                                    {data.paymentReference || '-'}
                                </span>
                            </dd>
                        </div>
                    </dl>
                </div>
                <div className="Row">
                    <h4>Amount & purpose</h4>
                    <dl>
                        {fee && feeOptions.length > 1 && (
                            <div className={styles.Row}>
                                <dt>Fee:</dt>
                                <dd>{getFeeLabel(fee.bankFeeSplitType).label}</dd>
                            </div>
                        )}
                        <div className={styles.Row}>
                            <dt>Amount:</dt>
                            <dd>
                                {data.amount}{' '}
                                <AssetCodeFormatter
                                    assetCode={data.payeeConnectionAssetAccount?.currencyCode ?? ''}
                                />
                            </dd>
                        </div>
                        <div className={styles.Row}>
                            <dt><span className="notranslate" translate="no">{data.transferType}</span> Payment Purpose:</dt>
                            <dd>{purpose ? purpose.label : data.purpose || '-'}</dd>
                        </div>
                    </dl>
                </div>
            </div>

            <h4>More details</h4>
            <dl>
                {!bFinancialInstitution && (
                    <div className={styles.Row}>
                        <dt>Memo:</dt>
                        <dd>{data?.memo || '-'}</dd>
                    </div>
                )}
                <div className={styles.Row}>
                    <dt>Attachments:</dt>
                    <dd>
                        {data.attachments.length > 0
                            ? data.attachments.reduce(
                                  (prev, cur, i) => prev + `${i !== 0 ? ', ' : ''}${cur.name}`,
                                  ''
                              )
                            : '-'}
                    </dd>
                </div>
                <div className={styles.Row}>
                    <dt>Notes:</dt>
                    <dd>{data.notes || '-'}</dd>
                </div>
            </dl>

            <Button
                disabled={isSubmitting}
                onClick={openMfaModal}
                color="third"
                showSubmittingSpinner={isSubmitting}
            >
                {t('confirmPayment')}
            </Button>
        </div>
    );
};
